import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ElementWidthClassDirective } from '@app/shared/directives/element-width-class-directive';
import { FpCorrelationGraphStore } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-graph/services/fp-correlation-graph/fp-correlation-graph.store';
import { FpCorrelationGraphInfoComponent } from '../fp-correlation-graph-info/fp-correlation-graph-info.component';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { FloatingPopupLineChartWrapperComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-line-chart-wrapper/floating-popup-line-chart-wrapper.component';
import { FpCorrelationGraphChartService } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-graph/services/fp-correlation-graph-chart.service';
import { FloatingPopupContentChartActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-chart-actions/floating-popup-content-chart-actions.component';
import { FloatingPopupContentExportCsvActionsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-content-export-csv-actions/floating-popup-content-export-csv-actions.component';
import { FpOpenCustomCorrelationActionComponent } from '@app/main/leak-detection/components/fp-open-custom-correlation-action/fp-open-custom-correlation-action.component';
import { FpCorrelationGraphExportService } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-graph/services/fp-correlation-graph-export.service';
import { IFloatingPopupInstance } from '@app/main/main-content/floating-popup-manager/interfaces/floating-popup-instance';
import { FpCorrelationGraphActionType } from '@app/main/leak-detection/floating-popups/floating-popup-correlation/tabs/fp-correlation-graph/services/fp-correlation-graph/fp-correlation-graph.actions';
import { FloatingPopupService } from '@app/main/main-content/floating-popup-manager/services/floating-popup.service';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';

@Component({
  selector: 'gc-fp-correlation-graph',
  standalone: true,
  imports: [
    ElementWidthClassDirective,
    FpCorrelationGraphInfoComponent,
    FpOpenCustomCorrelationActionComponent,
    FloatingPopupLineChartWrapperComponent,
    FloatingPopupContentChartActionsComponent,
    FloatingPopupContentExportCsvActionsComponent,
    FloatingPopupBaseErrorMessageComponent,
  ],
  templateUrl: './fp-correlation-graph.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'min-h-0 grow-1',
  },
})
export class FpCorrelationGraphComponent {
  protected readonly screenResolutionService = inject(ScreenResolutionService);
  public readonly currentTabStore = inject(FpCorrelationGraphStore);
  protected readonly translateService = inject(TranslateService);
  protected readonly chartService = inject(FpCorrelationGraphChartService);
  protected readonly fpCorrelationGraphExportService = inject(FpCorrelationGraphExportService);
  protected readonly floatingPopupService = inject(FloatingPopupService);

  public popup = input<IFloatingPopupInstance>();

  public isChartFullscreen = toSignal(this.currentTabStore.isChartFullscreenActive$, {
    initialValue: false,
  });

  private readonly popupMainData = toSignal(this.currentTabStore.popupMainData$, {
    initialValue: null,
  });

  private readonly data = toSignal(this.currentTabStore.data$, {
    initialValue: null,
  });

  public error = toSignal(this.currentTabStore.error$, {
    initialValue: null,
  });

  public isChartDataExist = computed(() => !!this.data()?.length);

  public readonly correlation = computed(() => this.popupMainData()?.correlation);

  public combinedChartData = computed(() => {
    const data = this.data() || [];
    const measurementSystem = this.currentTabStore.viewMeasurementSystem();

    const chartData = this.chartService.getDataForChart(data, measurementSystem);
    const chartOptions = this.chartService.generateOptions(data, measurementSystem);

    return { chartData, chartOptions };
  });

  public doExport() {
    const popupMainData = this.popupMainData();

    if (!popupMainData) {
      return;
    }

    const data = this.data() || [];
    const measurementSystem = this.currentTabStore.viewMeasurementSystem();

    this.fpCorrelationGraphExportService.exportCsv(popupMainData, data, measurementSystem);
  }

  public toggleChartFullscreen() {
    this.currentTabStore.dispatch({
      type: FpCorrelationGraphActionType.ToggleChartFullscreen,
      synchronizeWithPopupId: this.popup()?.id || undefined,
    });
  }
}
